import { createSelector } from '@reduxjs/toolkit';

import { Direction2D, LineSegment2D, Point2D } from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getActiveComponentDesignCenter } from '../../store/selectors/boundsSelectors';
import { getCurrentRenderMode } from '../../store/slices/camera/selectors';

const DIMENSION_TICK_ANGLE_IN_DEGREES = 45;

const LAYOUT_ENDPOINT_MARK_LENGTH = 7;
const DESIGN_ENDPOINT_MARK_LENGTH = 4;

export const STANDARD_ENDPOINT_MARK_RATIOS = { farther: 0.9, shorter: 0.5 } as const;
const CENTERED_ENDPOINT_MARK_RATIOS = { farther: 0.6, shorter: 0.6 } as const;

export interface EndpointMarkConfiguration {
  readonly length: number;
  readonly extendFartherToward: Point2D | null;
  readonly extensionRatios: { farther: number; shorter: number };
}

export const LAYOUT_ENDPOINT_MARK: EndpointMarkConfiguration = {
  length: LAYOUT_ENDPOINT_MARK_LENGTH,
  extendFartherToward: null,
  extensionRatios: STANDARD_ENDPOINT_MARK_RATIOS,
};

export const CENTERED_LAYOUT_ENDPOINT_MARK: EndpointMarkConfiguration = {
  ...LAYOUT_ENDPOINT_MARK,
  extensionRatios: CENTERED_ENDPOINT_MARK_RATIOS,
};

export const getEndpointMarkConfiguration = createSelector(
  getCurrentRenderMode,
  getActiveComponentDesignCenter,
  (renderMode, center): EndpointMarkConfiguration => {
    switch (renderMode) {
      case 'design':
        return {
          length: DESIGN_ENDPOINT_MARK_LENGTH,
          extendFartherToward: center == null ? null : new Point2D(center.x, center.y),
          extensionRatios: STANDARD_ENDPOINT_MARK_RATIOS,
        };

      case 'layout':
        return LAYOUT_ENDPOINT_MARK;

      default:
        return assertExhaustiveSwitch(renderMode);
    }
  },
);

export interface EndpointMark {
  readonly start: Point2D;
  readonly end: Point2D;

  // The center, direction, and length are precomputed so consumers don't re-derive them from the ends.
  readonly center: Point2D;
  readonly direction: Direction2D;
  readonly length: number;
}

interface EndpointMarkSegments {
  readonly ticks: readonly EndpointMark[];
  readonly extensionMarks: readonly EndpointMark[];
}

export function buildEndpointMarkSegments(
  dimension: LineSegment2D,
  { length, extendFartherToward, extensionRatios }: EndpointMarkConfiguration,
): EndpointMarkSegments {
  const tickDirection = dimension.direction.rotate(DIMENSION_TICK_ANGLE_IN_DEGREES);

  const perpendicularDirection = dimension.direction.rotateQuarterTurn();
  const fartherExtensionDirection = extendFartherToward
    ? perpendicularDirection.orientWith(dimension.midpoint.directionToPoint(extendFartherToward))
    : perpendicularDirection;

  const fartherReach = length * extensionRatios.farther;
  const shorterReach = length * extensionRatios.shorter;

  const { endPoints } = dimension;
  return {
    ticks: endPoints.map((endPoint) => buildMark(endPoint, tickDirection, length / 2, length / 2)),
    extensionMarks: endPoints.map((endPoint) =>
      buildMark(endPoint, fartherExtensionDirection, fartherReach, shorterReach),
    ),
  };
}

function buildMark(point: Point2D, direction: Direction2D, fartherReach: number, shorterReach: number): EndpointMark {
  return {
    start: point.translate(direction.toVector(-shorterReach)),
    end: point.translate(direction.toVector(fartherReach)),
    center: point.translate(direction.toVector((fartherReach - shorterReach) / 2)),
    direction,
    length: fartherReach + shorterReach,
  };
}
